import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaTiktok } from "react-icons/fa";
import { listedTowns } from "../data/towns";
import { openCookieSettings } from "../lib/consent";
import { SOCIAL } from "../lib/site";

const cityPath = (slug) => `/${slug}/party-decorations`;
const BUSINESS_PHONE = "+44 7762 230267";
const BUSINESS_PHONE_LINK = "+447762230267";
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/fNmsGsPUhQAPpjVP8";
const FEATURED_AREA_SLUGS = [
  "belfast",
  "lisburn",
  "bangor",
  "newtownabbey",
  "newtownards",
  "antrim",
];
const EXPLORE_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/services/surprise-room-decor", label: "Surprise Room Décor" },
  { to: "/services/venue-decor", label: "Venue decor" },
  { to: "/packages", label: "Packages" },
  { to: "/packages/classic-celebration", label: "Classic Celebration" },
  { to: "/packages/luxury-look", label: "Luxury Look" },
];
const COMPANY_LINKS = [
  { to: "/about", label: "About us" },
  { to: "/contact", label: "Contact" },
  { to: "/privacy-policy", label: "Privacy policy" },
  { to: "/cancellation-and-refunds", label: "Cancellations" },
  { to: "/northern-ireland/party-decorations", label: "Areas we cover" },
  { to: "/birthday-party-decorators-belfast", label: "Birthday decor Belfast" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  const AREAS = useMemo(
    () => [...listedTowns].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );
  const featuredAreas = useMemo(
    () =>
      FEATURED_AREA_SLUGS.map((slug) => AREAS.find((area) => area.slug === slug)).filter(Boolean),
    [AREAS]
  );
  const [areasOpen, setAreasOpen] = useState(false);

  return (
    <footer
      className="border-t border-rose-100 bg-gradient-to-b from-rose-50 via-white to-white"
      itemScope
      itemType="https://schema.org/LocalBusiness"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8 md:py-12">
          <div className="grid gap-10 border-t border-rose-100 py-8 md:py-10 lg:grid-cols-12">
              <section className="lg:col-span-4">
                <Link to="/" className="inline-block" itemProp="url">
                  <span className="text-lg font-semibold tracking-tight text-slate-900" itemProp="name">
                    Party Crafters
                  </span>
                </Link>
                <p className="mt-4 max-w-md text-sm leading-6 text-slate-600">
                  <span itemProp="description">
                    Belfast-based party decorators and event decorators for birthdays, baby showers,
                    anniversaries, engagements and celebrations across Northern Ireland. Choose party
                    packages, balloon arches and garlands, or full venue decor, with setup, takedown and
                    installs planned around your venue, timings and event brief.
                  </span>
                </p>
                <div
                  className="mt-5 space-y-3 text-sm text-slate-600"
                  itemProp="address"
                  itemScope
                  itemType="https://schema.org/PostalAddress"
                >
                  <p>
                    <span className="font-medium text-slate-900">Based in </span>
                    <span itemProp="addressLocality">Belfast</span>,{" "}
                    <span itemProp="addressRegion">Northern Ireland</span>
                  </p>
                  <p>
                    <span className="font-medium text-slate-900">Open daily </span>
                    9:00am to 8:00pm for enquiries and bookings
                  </p>
                </div>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <a
                    href={`tel:${BUSINESS_PHONE_LINK}`}
                    className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                    itemProp="telephone"
                  >
                    Call {BUSINESS_PHONE}
                  </a>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <a
                    href={SOCIAL.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white transition hover:border-rose-200 hover:bg-rose-50"
                    itemProp="sameAs"
                  >
                    <img src="/icons/instagram.svg" alt="Instagram" className="h-5 w-5" loading="lazy" />
                  </a>
                  <a
                    href={SOCIAL.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white transition hover:border-rose-200 hover:bg-rose-50"
                    itemProp="sameAs"
                  >
                    <img src="/icons/facebook.svg" alt="Facebook" className="h-5 w-5" loading="lazy" />
                  </a>
                  <a
                    href={SOCIAL.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="TikTok"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white transition hover:border-rose-200 hover:bg-rose-50"
                    itemProp="sameAs"
                  >
                    <FaTiktok className="h-5 w-5 text-slate-900" />
                  </a>
                  <a
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    Google Business Profile
                  </a>
                </div>
              </section>

              <nav className="lg:col-span-2" aria-label="Popular pages">
                <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-900">
                  Popular pages
                </h3>
                <ul className="mt-4 space-y-3 text-sm text-slate-600">
                  {EXPLORE_LINKS.map((link) => (
                    <li key={link.to}>
                      <Link className="transition hover:text-slate-900" to={link.to}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              <nav className="lg:col-span-2" aria-label="Company">
                <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-900">
                  Company
                </h3>
                <ul className="mt-4 space-y-3 text-sm text-slate-600">
                  {COMPANY_LINKS.map((link) => (
                    <li key={link.to}>
                      <Link className="transition hover:text-slate-900" to={link.to}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      onClick={openCookieSettings}
                      className="transition hover:text-slate-900"
                    >
                      Cookie settings
                    </button>
                  </li>
                </ul>
              </nav>

              <section className="lg:col-span-4">
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-900">
                      Areas we cover
                    </h3>
                    <button
                      type="button"
                      onClick={() => setAreasOpen((value) => !value)}
                      aria-expanded={areasOpen}
                      aria-controls="footer-areas-panel"
                      className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 transition hover:text-slate-900"
                    >
                      <span>{areasOpen ? "Hide full town list" : "View full town list"}</span>
                      <svg
                        className={`h-4 w-4 transition-transform ${areasOpen ? "rotate-180" : ""}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.13l3.71-3.9a.75.75 0 111.08 1.04l-4.24 4.46a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
                      </svg>
                    </button>
                  </div>

                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    We cover Belfast and surrounding towns across Northern Ireland for venue decor,
                    balloon styling and celebration backdrops.
                  </p>

                  {!areasOpen ? (
                    <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                      {featuredAreas.map((area) => (
                        <li key={area.slug}>
                          <Link
                            to={cityPath(area.slug)}
                            className="flex items-center justify-between rounded-2xl border border-white bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:border-rose-200 hover:text-slate-900"
                            itemProp="areaServed"
                          >
                            <span>{area.name}</span>
                            <span className="text-slate-400">→</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div
                    id="footer-areas-panel"
                    className={`overflow-hidden transition-all duration-300 ${
                      areasOpen ? "mt-4 max-h-80" : "max-h-0"
                    }`}
                    aria-hidden={!areasOpen}
                  >
                    <div className="rounded-2xl border border-white bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Full town list
                      </p>
                      <ul className="mt-3 grid max-h-56 gap-x-5 gap-y-2 overflow-y-auto pr-2 text-sm text-slate-600 sm:grid-cols-2">
                        {AREAS.map((town) => (
                          <li key={town.slug}>
                            <Link
                              to={cityPath(town.slug)}
                              className="transition hover:text-slate-900 hover:underline underline-offset-4"
                            >
                              {town.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </section>
          </div>

          <div className="flex flex-col gap-3 border-t border-rose-100 py-5 text-sm text-slate-500 lg:flex-row lg:items-center lg:justify-between">
            <p>
              © {year} Party Crafters. All rights reserved.
            </p>
            <p>
              Website by{" "}
              <a
                href="https://solidsite.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 transition hover:text-slate-700"
              >
                solidsite.uk
              </a>
            </p>
          </div>
          <div className="pb-[calc(env(safe-area-inset-bottom)+96px)] md:pb-12" />
        </div>
      </div>
    </footer>
  );
}
